import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import useSWR, { mutate } from 'swr';
import {
    Skeleton,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
} from '@mui/material';

import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import SparaTitle from 'components/SparaTitle/SparaTitle';
import SparaButton from 'components/SparaButton/SparaButton';

import { fetcher, jobBatchApi, queueHealthApi } from '../../lib/api';
import styles from './JobBatches.module.scss';

const PAGE_SIZE = 30;
const REFRESH_MS = 3000;

const STATUS_STYLES = {
    running: { background: 'var(--spara-badge-info-bg)', color: 'var(--spara-badge-info-fg)' },
    finished: { background: 'var(--spara-badge-green-bg)', color: 'var(--spara-badge-green-fg)' },
    failed: { background: 'var(--spara-badge-fail-bg)', color: 'var(--spara-badge-fail-fg)' },
    cancelled: {
        background: 'var(--spara-badge-orange-bg)',
        color: 'var(--spara-badge-orange-fg)',
    },
};

const TYPE_STYLES = {
    special_user_import: {
        background: 'var(--spara-badge-purple-bg)',
        color: 'var(--spara-badge-purple-fg)',
    },
    offer_notification: {
        background: 'var(--spara-badge-cyan-bg)',
        color: 'var(--spara-badge-cyan-fg)',
    },
    hr_sync: {
        background: 'var(--spara-badge-indigo-bg)',
        color: 'var(--spara-badge-indigo-fg)',
    },
    other: {
        background: 'var(--spara-badge-neutral-bg)',
        color: 'var(--spara-badge-neutral-fg)',
    },
};

const mergeBatches = (prev, incoming) => {
    const byId = new Map(prev.map((row) => [row.id, row]));
    for (const row of incoming) {
        byId.set(row.id, row);
    }
    return Array.from(byId.values()).sort(
        (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
    );
};

const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '—';
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}.${mm}.${yyyy} ${hh}:${min}`;
};

const formatAge = (seconds) => {
    if (seconds == null) return '—';
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    return `${Math.floor(seconds / 3600)}h`;
};

const RowSkeleton = () => (
    <TableRow>
        {Array.from({ length: 9 }).map((_, i) => (
            <TableCell key={i}>
                <Skeleton variant="text" width={i === 0 ? 120 : 70} />
            </TableCell>
        ))}
    </TableRow>
);

const JobBatches = () => {
    const { t } = useTranslation();
    const [status, setStatus] = useState('all');
    const [page, setPage] = useState(1);
    const [batches, setBatches] = useState([]);
    const [cancellingId, setCancellingId] = useState(null);
    const [selectedId, setSelectedId] = useState(null);

    const listUrl = jobBatchApi.list({
        page,
        limit: PAGE_SIZE,
        ...(status !== 'all' ? { status } : {}),
    });

    const { data, error, isLoading } = useSWR(listUrl, fetcher, {
        refreshInterval: (latest) => {
            const rows = latest?.results || [];
            const hasRunning = rows.some((b) => b.status === 'running');
            return hasRunning ? REFRESH_MS : 15000;
        },
    });

    const { data: healthData } = useSWR(queueHealthApi.get(), fetcher, {
        refreshInterval: 10000,
    });
    const health = healthData?.results;

    const detailUrl = selectedId ? jobBatchApi.get(selectedId) : null;
    const { data: detailData, isLoading: detailLoading } = useSWR(detailUrl, fetcher, {
        refreshInterval: selectedId ? 5000 : 0,
    });
    const detail = detailData?.results;

    useEffect(() => {
        if (!data?.results) return;
        setBatches((prev) => (page === 1 ? data.results : mergeBatches(prev, data.results)));
    }, [data, page]);

    const totalCount = data?.count ?? batches.length;

    const statusFilters = useMemo(
        () => [
            { id: 'all', label: t('job_batches.filter_all') },
            { id: 'running', label: t('job_batches.filter_running') },
            { id: 'finished', label: t('job_batches.filter_finished') },
            { id: 'failed', label: t('job_batches.filter_failed') },
            { id: 'cancelled', label: t('job_batches.filter_cancelled') },
        ],
        [t]
    );

    const typeLabel = (type) => {
        if (type === 'special_user_import') return t('job_batches.type_member_import');
        if (type === 'offer_notification') return t('job_batches.badge_notification_send');
        if (type === 'hr_sync') return t('job_batches.type_hr_sync');
        return t('job_batches.type_other');
    };

    const listBadge = (batch) => {
        if (
            batch.is_notification_send ||
            batch.notification_delivery ||
            batch.type === 'offer_notification'
        ) {
            return {
                label: t('job_batches.badge_notification_send'),
                style: TYPE_STYLES.offer_notification,
            };
        }
        if (batch.type === 'special_user_import') {
            return {
                label: t('job_batches.type_member_import'),
                style: TYPE_STYLES.special_user_import,
            };
        }
        if (batch.type === 'hr_sync') {
            return {
                label: t('job_batches.type_hr_sync'),
                style: TYPE_STYLES.hr_sync,
            };
        }
        return {
            label: t('job_batches.type_other'),
            style: TYPE_STYLES.other,
        };
    };

    const statusLabel = (s) => {
        if (s === 'running') return t('job_batches.status_running');
        if (s === 'finished') return t('job_batches.status_finished');
        if (s === 'failed') return t('job_batches.status_failed');
        if (s === 'cancelled') return t('job_batches.status_cancelled');
        return s;
    };

    const handleFilter = (next) => {
        setStatus(next);
        setPage(1);
        setBatches([]);
    };

    const handleCancel = async (batchId, e) => {
        e?.stopPropagation?.();
        if (!window.confirm(t('job_batches.cancel_confirm'))) return;
        setCancellingId(batchId);
        try {
            await fetcher(jobBatchApi.cancel(batchId), 'POST');
            mutate(listUrl);
            if (selectedId === batchId) mutate(detailUrl);
        } catch (err) {
            window.alert(err?.response?.data?.message || err?.message || t('common.error'));
        } finally {
            setCancellingId(null);
        }
    };

    const hasMore = batches.length > 0 && batches.length < totalCount;

    return (
        <GridContainer>
            <GridItem xs={12}>
                <SparaTitle title={t('menu.job_batches')} />
            </GridItem>
            <GridItem xs={12}>
                <Card>
                    <CardBody>
                        {health && (
                            <div
                                className={`${styles.healthBar} ${
                                    health.worker_behind ? styles.healthBarBehind : ''
                                }`}
                            >
                                <div className={styles.healthStat}>
                                    <div className={styles.healthLabel}>
                                        {t('queue_health.pending')}
                                    </div>
                                    <div className={styles.healthValue}>
                                        {health.pending_jobs ?? '—'}
                                    </div>
                                </div>
                                <div className={styles.healthStat}>
                                    <div className={styles.healthLabel}>
                                        {t('queue_health.failed')}
                                    </div>
                                    <div className={styles.healthValue}>
                                        {health.failed_jobs ?? 0}
                                    </div>
                                </div>
                                <div className={styles.healthStat}>
                                    <div className={styles.healthLabel}>
                                        {t('queue_health.running_batches')}
                                    </div>
                                    <div className={styles.healthValue}>
                                        {health.running_batches ?? 0}
                                    </div>
                                </div>
                                <div className={styles.healthStat}>
                                    <div className={styles.healthLabel}>
                                        {t('queue_health.oldest_batch')}
                                    </div>
                                    <div className={styles.healthValue}>
                                        {formatAge(health.oldest_running_batch_age_seconds)}
                                    </div>
                                </div>
                                <div className={styles.healthStat}>
                                    <div className={styles.healthLabel}>
                                        {t('queue_health.status')}
                                    </div>
                                    <div className={styles.healthValue}>
                                        {health.worker_behind
                                            ? t('queue_health.behind')
                                            : t('queue_health.ok')}
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className={styles.listHeader}>
                            <div className={styles.listHeaderCount}>
                                {totalCount} {t('menu.job_batches').toLowerCase()}
                            </div>
                            <div className={styles.filters}>
                                {statusFilters.map((f) => (
                                    <button
                                        key={f.id}
                                        type="button"
                                        className={`${styles.filterChip} ${
                                            status === f.id ? styles.filterChipActive : ''
                                        }`}
                                        onClick={() => handleFilter(f.id)}
                                    >
                                        {f.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {error && (
                            <div className={styles.empty}>
                                {error?.response?.data?.message || t('common.error')}
                            </div>
                        )}

                        <div className={styles.tableWrap}>
                            <Table>
                                <TableHead className={styles.tableHead}>
                                    <TableRow>
                                        <TableCell>{t('job_batches.col_type')}</TableCell>
                                        <TableCell>{t('job_batches.col_status')}</TableCell>
                                        <TableCell>{t('job_batches.col_progress')}</TableCell>
                                        <TableCell>{t('job_batches.col_jobs')}</TableCell>
                                        <TableCell>{t('job_batches.col_delivery')}</TableCell>
                                        <TableCell>{t('job_batches.col_related')}</TableCell>
                                        <TableCell>{t('job_batches.col_created_by')}</TableCell>
                                        <TableCell>{t('job_batches.col_created')}</TableCell>
                                        <TableCell>{t('actions')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody className={styles.tableBody}>
                                    {isLoading &&
                                        page === 1 &&
                                        batches.length === 0 &&
                                        Array.from({ length: 8 }).map((_, i) => (
                                            <RowSkeleton key={i} />
                                        ))}
                                    {!isLoading && batches.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={9}>
                                                <div className={styles.empty}>
                                                    {t('job_batches.empty')}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {batches.map((batch) => (
                                        <TableRow
                                            key={batch.id}
                                            className={styles.clickableRow}
                                            onClick={() => setSelectedId(batch.id)}
                                        >
                                            <TableCell>
                                                {(() => {
                                                    const badge = listBadge(batch);
                                                    return (
                                                        <span
                                                            className={styles.badge}
                                                            style={badge.style}
                                                        >
                                                            {badge.label}
                                                        </span>
                                                    );
                                                })()}
                                                <div className={styles.mono} title={batch.id}>
                                                    {batch.id}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <span
                                                    className={styles.badge}
                                                    style={
                                                        STATUS_STYLES[batch.status] ||
                                                        STATUS_STYLES.running
                                                    }
                                                >
                                                    {statusLabel(batch.status)}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                <div className={styles.progressTrack}>
                                                    <div
                                                        className={styles.progressFill}
                                                        style={{
                                                            width: `${Math.min(
                                                                100,
                                                                batch.progress || 0
                                                            )}%`,
                                                        }}
                                                    />
                                                </div>
                                                <div className={styles.progressLabel}>
                                                    {batch.progress ?? 0}%
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                {batch.total_jobs - batch.pending_jobs}/
                                                {batch.total_jobs}
                                                {batch.failed_jobs > 0 && (
                                                    <div className={styles.progressLabel}>
                                                        {batch.failed_jobs}{' '}
                                                        {t('job_batches.failed_jobs')}
                                                    </div>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {batch.notification_delivery ? (
                                                    <>
                                                        <div>
                                                            {batch.notification_delivery.sent}/
                                                            {batch.notification_delivery.total}{' '}
                                                            {t('job_batches.delivery_sent')}
                                                        </div>
                                                        {(batch.notification_delivery.failed > 0 ||
                                                            batch.notification_delivery.pending >
                                                                0) && (
                                                            <div className={styles.progressLabel}>
                                                                {batch.notification_delivery
                                                                    .failed > 0 &&
                                                                    `${batch.notification_delivery.failed} ${t('job_batches.delivery_failed')}`}
                                                                {batch.notification_delivery
                                                                    .failed > 0 &&
                                                                    batch.notification_delivery
                                                                        .pending > 0 &&
                                                                    ' · '}
                                                                {batch.notification_delivery
                                                                    .pending > 0 &&
                                                                    `${batch.notification_delivery.pending} ${t('job_batches.delivery_pending')}`}
                                                            </div>
                                                        )}
                                                    </>
                                                ) : (
                                                    '—'
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {batch.related_brief?.kind === 'offer' &&
                                                batch.related_brief?.id ? (
                                                    <Link
                                                        className={styles.offerLink}
                                                        to={`/offers/${batch.related_brief.id}`}
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        {batch.related_brief.label}
                                                    </Link>
                                                ) : batch.related_brief?.kind === 'groups' &&
                                                  batch.related_brief?.id ? (
                                                    <Link
                                                        className={styles.offerLink}
                                                        to={`/group/${batch.related_brief.id}`}
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        {batch.related_brief.label}
                                                    </Link>
                                                ) : (
                                                    batch.related_brief?.label || '—'
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {batch.created_by?.name || '—'}
                                            </TableCell>
                                            <TableCell>{formatDate(batch.created_at)}</TableCell>
                                            <TableCell>
                                                {batch.status === 'running' ? (
                                                    <button
                                                        type="button"
                                                        className={styles.cancelBtn}
                                                        disabled={cancellingId === batch.id}
                                                        onClick={(e) => handleCancel(batch.id, e)}
                                                    >
                                                        {t('job_batches.cancel')}
                                                    </button>
                                                ) : (
                                                    '—'
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>

                        {hasMore && (
                            <div className={styles.loadMore}>
                                <SparaButton
                                    color="primary"
                                    onClick={() => setPage((p) => p + 1)}
                                >
                                    {t('job_batches.load_more')}
                                </SparaButton>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </GridItem>

            {selectedId && (
                <div className={styles.overlay} onClick={() => setSelectedId(null)}>
                    <div className={styles.drawer} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.drawerHeader}>
                            <div>
                                <div className={styles.drawerTitle}>
                                    {t('job_batches.detail_title')}
                                    {detail?.is_notification_send && (
                                        <span
                                            className={styles.badge}
                                            style={{
                                                marginLeft: 8,
                                                ...TYPE_STYLES.offer_notification,
                                            }}
                                        >
                                            {t('job_batches.badge_notification_send')}
                                        </span>
                                    )}
                                </div>
                                <div className={styles.mono}>{selectedId}</div>
                                {detail?.lookup_kind && (
                                    <div className={styles.progressLabel}>
                                        {detail.lookup_kind === 'notification_batch'
                                            ? t('job_batches.lookup_notification_batch')
                                            : t('job_batches.lookup_job_batch')}
                                    </div>
                                )}
                            </div>
                            <button
                                type="button"
                                className={styles.drawerClose}
                                onClick={() => setSelectedId(null)}
                            >
                                ×
                            </button>
                        </div>
                        <div className={styles.drawerBody}>
                            {detailLoading && !detail && <Skeleton variant="rectangular" height={120} />}
                            {detail && (
                                <>
                                    <div className={styles.metaGrid}>
                                        <div>
                                            <div className={styles.metaLabel}>
                                                {t('job_batches.col_type')}
                                            </div>
                                            <div className={styles.metaValue}>
                                                {typeLabel(detail.type)}
                                            </div>
                                        </div>
                                        <div>
                                            <div className={styles.metaLabel}>
                                                {t('job_batches.col_status')}
                                            </div>
                                            <div className={styles.metaValue}>
                                                {statusLabel(detail.status)}
                                            </div>
                                        </div>
                                        <div>
                                            <div className={styles.metaLabel}>
                                                {t('job_batches.col_created_by')}
                                            </div>
                                            <div className={styles.metaValue}>
                                                {detail.created_by?.name || '—'}
                                                {detail.created_by?.email
                                                    ? ` (${detail.created_by.email})`
                                                    : ''}
                                            </div>
                                        </div>
                                        <div>
                                            <div className={styles.metaLabel}>
                                                {t('job_batches.col_progress')}
                                            </div>
                                            <div className={styles.metaValue}>
                                                {detail.progress}% —{' '}
                                                {detail.total_jobs - detail.pending_jobs}/
                                                {detail.total_jobs}
                                            </div>
                                        </div>
                                        <div>
                                            <div className={styles.metaLabel}>
                                                {t('job_batches.col_created')}
                                            </div>
                                            <div className={styles.metaValue}>
                                                {formatDate(detail.created_at)}
                                            </div>
                                        </div>
                                        <div>
                                            <div className={styles.metaLabel}>
                                                {t('job_batches.col_finished')}
                                            </div>
                                            <div className={styles.metaValue}>
                                                {formatDate(
                                                    detail.cancelled_at || detail.finished_at
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {detail.related?.offer && (
                                        <div>
                                            <div className={styles.sectionTitle}>
                                                {t('job_batches.related_offer')}
                                            </div>
                                            <Link
                                                className={styles.offerLink}
                                                to={`/offers/${detail.related.offer.id}`}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                {detail.related.offer.name} (#{detail.related.offer.id})
                                            </Link>
                                        </div>
                                    )}

                                    {detail.notification_delivery && (
                                        <div>
                                            <div className={styles.sectionTitle}>
                                                {t('job_batches.delivery_title')}
                                            </div>
                                            <div className={styles.deliveryStats}>
                                                <div className={styles.deliveryStat}>
                                                    <div className={styles.deliveryStatValue}>
                                                        {detail.notification_delivery.sent}
                                                    </div>
                                                    <div className={styles.deliveryStatLabel}>
                                                        {t('job_batches.delivery_sent')}
                                                    </div>
                                                </div>
                                                <div className={styles.deliveryStat}>
                                                    <div className={styles.deliveryStatValue}>
                                                        {detail.notification_delivery.pending}
                                                    </div>
                                                    <div className={styles.deliveryStatLabel}>
                                                        {t('job_batches.delivery_pending')}
                                                    </div>
                                                </div>
                                                <div
                                                    className={`${styles.deliveryStat} ${styles.deliveryStatFailed}`}
                                                >
                                                    <div className={styles.deliveryStatValue}>
                                                        {detail.notification_delivery.failed}
                                                    </div>
                                                    <div className={styles.deliveryStatLabel}>
                                                        {t('job_batches.delivery_failed')}
                                                    </div>
                                                </div>
                                                <div className={styles.deliveryStat}>
                                                    <div className={styles.deliveryStatValue}>
                                                        {detail.notification_delivery.total}
                                                    </div>
                                                    <div className={styles.deliveryStatLabel}>
                                                        {t('job_batches.delivery_total')}
                                                    </div>
                                                </div>
                                            </div>
                                            {(detail.notification_delivery.batches || []).map(
                                                (nb) => (
                                                    <button
                                                        key={nb.id}
                                                        type="button"
                                                        className={styles.mono}
                                                        style={{
                                                            display: 'block',
                                                            border: 'none',
                                                            background: 'transparent',
                                                            padding: 0,
                                                            cursor: 'pointer',
                                                            textAlign: 'left',
                                                            color: 'var(--spara-badge-info-fg)',
                                                        }}
                                                        onClick={() => setSelectedId(nb.id)}
                                                        title={t('job_batches.open_uuid')}
                                                    >
                                                        notification_batch: {nb.id}
                                                    </button>
                                                )
                                            )}

                                            {(detail.notification_delivery.failed_logs || [])
                                                .length > 0 && (
                                                <>
                                                    <div className={styles.sectionTitle}>
                                                        {t('job_batches.delivery_failed_logs')}
                                                    </div>
                                                    {detail.notification_delivery.failed_logs.map(
                                                        (log) => (
                                                            <div
                                                                key={log.id}
                                                                className={styles.failedItem}
                                                            >
                                                                <div
                                                                    className={
                                                                        styles.progressLabel
                                                                    }
                                                                >
                                                                    #{log.id} ·{' '}
                                                                    {formatDate(log.updated_at)}
                                                                </div>
                                                                <div className={styles.exceptionBox}>
                                                                    {log.error_message || '—'}
                                                                </div>
                                                            </div>
                                                        )
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}

                                    {(detail.linked_job_batches || []).length > 0 && (
                                        <div>
                                            <div className={styles.sectionTitle}>
                                                {t('job_batches.linked_keyrslur')}
                                            </div>
                                            {detail.linked_job_batches.map((jb) => (
                                                <button
                                                    key={jb.id}
                                                    type="button"
                                                    className={styles.failedItem}
                                                    style={{
                                                        width: '100%',
                                                        textAlign: 'left',
                                                        cursor: 'pointer',
                                                    }}
                                                    onClick={() => setSelectedId(jb.id)}
                                                >
                                                    <div className={styles.metaValue}>
                                                        {jb.is_notification_send
                                                            ? t(
                                                                  'job_batches.badge_notification_send'
                                                              )
                                                            : typeLabel(jb.type)}
                                                        {' · '}
                                                        {statusLabel(jb.status)}
                                                    </div>
                                                    <div className={styles.mono}>{jb.id}</div>
                                                    <div className={styles.progressLabel}>
                                                        {jb.total_jobs - jb.pending_jobs}/
                                                        {jb.total_jobs} · {formatDate(jb.created_at)}
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    {detail.related?.groups?.length > 0 && (
                                        <div>
                                            <div className={styles.sectionTitle}>
                                                {t('job_batches.related_groups')}
                                            </div>
                                            {detail.related.groups.map((g) => (
                                                <div key={g.id} className={styles.metaValue}>
                                                    <Link
                                                        className={styles.offerLink}
                                                        to={`/group/${g.id}`}
                                                    >
                                                        {g.name} (#{g.id})
                                                    </Link>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <div className={styles.sectionTitle}>
                                        {t('job_batches.failed_details')} (
                                        {detail.failed_job_details?.length || 0})
                                    </div>
                                    {(detail.failed_job_details || []).length === 0 && (
                                        <div className={styles.progressLabel}>
                                            {t('job_batches.no_failed_details')}
                                        </div>
                                    )}
                                    {(detail.failed_job_details || []).map((fj) => (
                                        <div key={fj.uuid || fj.id} className={styles.failedItem}>
                                            <div className={styles.metaValue}>{fj.job}</div>
                                            <div className={styles.progressLabel}>
                                                {formatDate(fj.failed_at)} · {fj.queue}
                                            </div>
                                            <div className={styles.exceptionBox}>
                                                {fj.exception_summary || fj.exception}
                                            </div>
                                        </div>
                                    ))}

                                    {detail.lookup_kind === 'job_batch' &&
                                        detail.status === 'running' && (
                                        <div style={{ marginTop: 16 }}>
                                            <button
                                                type="button"
                                                className={styles.cancelBtn}
                                                disabled={cancellingId === detail.id}
                                                onClick={(e) => handleCancel(detail.id, e)}
                                            >
                                                {t('job_batches.cancel')}
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </GridContainer>
    );
};

export default JobBatches;
